import React, { forwardRef } from "react";
import { classNames } from "../../utils/common";
import linkStyles from "./Link.module.css";
import typographyStyles from "../Typography/Typography.module.css";
import { TypographyVariants } from "../Typography/Typography";

export type LinkProps = {
  onClick?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  onPointerDown?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  onKeyDown?: (evt: React.KeyboardEvent<HTMLAnchorElement>) => void;
  onMouseEnter?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseLeave?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLAnchorElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLAnchorElement>) => void;
  href: string;
  label: string;
  target?: string;
  rel?: string;
  leftIcon?: React.ReactElement;
  rightIcon?: React.ReactElement;
  variant?: `${TypographyVariants}`;
  isDisabled?: boolean;
  tabIndex?: number;
  dangerouslySetClassName?: string;
} & React.AriaAttributes;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    onClick,
    onPointerDown,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    href,
    label,
    target,
    rel,
    leftIcon,
    rightIcon,
    variant = TypographyVariants.BODY,
    isDisabled = false,
    tabIndex,
    dangerouslySetClassName = "",
    ...restProps
  } = props;

  const linkClassName = classNames({
    [linkStyles.catalystLink]: true,
    [typographyStyles[`catalystTypography${variant}`]]: true,
    [linkStyles.catalystLinkDisabled]: isDisabled
  });

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  const ariaProps: React.AriaAttributes = Object.entries(restProps)
    .filter(([key]) => key.startsWith("aria-"))
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  const dataProps = Object.entries(restProps)
    .filter(([key]) => key.startsWith("data-"))
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  return (
    <a
      ref={ref}
      className={classNames({
        [linkClassName]: true,
        [dangerouslySetClassName]: !!dangerouslySetClassName
      })}
      href={isDisabled ? undefined : href}
      onClick={handleClick}
      onPointerDown={isDisabled ? undefined : onPointerDown}
      onKeyDown={isDisabled ? undefined : onKeyDown}
      onMouseEnter={isDisabled ? undefined : onMouseEnter}
      onMouseLeave={isDisabled ? undefined : onMouseLeave}
      onFocus={isDisabled ? undefined : onFocus}
      onBlur={isDisabled ? undefined : onBlur}
      target={isDisabled ? undefined : target}
      rel={isDisabled ? undefined : rel}
      tabIndex={isDisabled ? -1 : tabIndex}
      aria-disabled={isDisabled}
      aria-label={label}
      {...ariaProps}
      {...dataProps}
    >
      {leftIcon && (
        <span className={linkStyles.catalystLinkIcon}>{leftIcon}</span>
      )}
      {label}
      {rightIcon && (
        <span className={linkStyles.catalystLinkIcon}>{rightIcon}</span>
      )}
    </a>
  );
});

Link.displayName = "Link";
